/* eslint-disable react-refresh/only-export-components -- context provider exports the useAuth hook alongside the provider component by design */
import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTheme } from '@/components/theme-provider'
import { fetchUserThemePreference } from '@/features/settings/utils/themeSyncUtils'
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { onSessionExpired } from '@/features/auth/authEvents';

interface User {
  id: number
  email: string
  username: string
  is_active: boolean
  is_superuser: boolean
  has_instructor_profile: boolean
  sequence_builder_tutorial_seen: boolean
}

interface ErrorResponse {
  detail: string
}

interface AuthContextType {
  user: User | null
  login: (username: string, password: string, options?: { navigateOnSuccess?: boolean | string }) => Promise<void>
  register: (email: string, username: string, password: string, options?: { navigateOnSuccess?: boolean | string }) => Promise<void>
  logout: () => Promise<void>
  isLoading: boolean
  isAuthenticating: boolean
}

const AuthContext = createContext<AuthContextType | null>(null)

const API_BASE = '/api/v1'

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isAuthenticating, setIsAuthenticating] = useState(false)
  const navigate = useNavigate()
  const { setTheme } = useTheme()
  const queryClient = useQueryClient();

  useEffect(() => {
    fetchUser()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount; must not refire when fetchUser identity changes per render
  }, [])

  // Listen for global session-expired events (fired by API helpers on 401).
  // The ref prevents duplicate handling when multiple 401s fire simultaneously.
  // It resets when the user logs back in (see fetchUser success path).
  const sessionExpiredHandled = useRef(false);
  const userRef = useRef(user);
  useEffect(() => { userRef.current = user; }, [user]);

  const handleSessionExpired = useCallback(() => {
    if (sessionExpiredHandled.current) return;
    sessionExpiredHandled.current = true;

    const wasLoggedIn = !!userRef.current;

    setUser(null);
    queryClient.resetQueries();

    // Only redirect and notify if the user had an active session that expired.
    // This avoids redirecting unauthenticated users on public pages (e.g. sequence builder).
    if (wasLoggedIn) {
      localStorage.removeItem('auth.wasLoggedIn');
      toast.error('Your session has expired. Please sign in again.');
      // Use window.location (not React Router's location) because this fires
      // from an event listener, not a render — the router location would be stale.
      const returnPath = window.location.pathname + window.location.search;
      navigate(`/login?from=${encodeURIComponent(returnPath)}`);
    }
  }, [queryClient, navigate]);

  useEffect(() => {
    return onSessionExpired(handleSessionExpired);
  }, [handleSessionExpired]);

  const fetchUser = async () => {
    try {
      const response = await fetch(`${API_BASE}/users/me`, {
        credentials: 'include'  // Important: sends cookies with request
      })

      if (!response.ok) {
        if (response.status === 401) {
          setUser(null)
          // Stale-tab case: this browser had a session previously but the
          // cookie is no longer accepted. handleSessionExpired's userRef
          // check can't catch this on initial mount because userRef is
          // still null before fetchUser resolves. The localStorage flag
          // carries the "I had been signed in" bit across reloads.
          if (localStorage.getItem('auth.wasLoggedIn')) {
            localStorage.removeItem('auth.wasLoggedIn')
            toast.error('Your session has expired. Please sign in again.')
            const returnPath = window.location.pathname + window.location.search
            navigate(`/login?from=${encodeURIComponent(returnPath)}`)
          }
          return
        }
        throw new Error('Failed to fetch user')
      }

      const userData = await response.json() as User
      setUser(userData)
      // Record that this browser has had a real session. Read by the 401
      // branch above on subsequent reloads to detect stale-tab expiry.
      localStorage.setItem('auth.wasLoggedIn', '1')

      // Re-arm the session-expired handler so it can fire again if this new session expires
      sessionExpiredHandled.current = false;

      // After successful user fetch, sync theme preference with server
      const themePreference = await fetchUserThemePreference()
      if (themePreference) {
        setTheme(themePreference)
      }
    } catch (error) {
      console.error('Error fetching user')
      setUser(null)
    } finally {
      setIsLoading(false)
    }
  }

  const login = async (username: string, password: string, options?: { navigateOnSuccess?: boolean | string }) => {
    setIsAuthenticating(true)
    return new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.onload = function() {
        if (xhr.status === 200) {
          fetchUser()
            .then(() => {
              setIsAuthenticating(false);
              resolve();
              if (options?.navigateOnSuccess === true) {
                navigate('/');
              } else if (typeof options?.navigateOnSuccess === 'string') {
                navigate(options.navigateOnSuccess);
              }
            })
            .catch(error => {
              setIsAuthenticating(false);
              console.error('Error fetching user after login:', error);
              reject(error);
            });
        } else {
          setIsAuthenticating(false);
          try {
            const errorData = JSON.parse(xhr.responseText) as ErrorResponse;
            reject(new Error(errorData.detail || 'Login failed'));
          } catch {
            reject(new Error('Login failed'));
          }
        }
      };

      xhr.onerror = function() {
        setIsAuthenticating(false);
        reject(new Error('Login failed'));
      };

      try {
        xhr.open('POST', `${API_BASE}/auth/token`, true);
        xhr.withCredentials = true;
        const formData = new FormData();
        formData.append('username', username);
        formData.append('password', password);
        formData.append('grant_type', 'password');
        xhr.send(formData);
      } catch (error) {
        setIsAuthenticating(false);
        console.error('Login error:', error);
        reject(error);
      }
    });
  }

  const register = async (email: string, username: string, password: string, options?: { navigateOnSuccess?: boolean | string }) => {
    setIsAuthenticating(true)
    return new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.onload = function() {
        if (xhr.status === 200) {
          fetchUser()
            .then(() => {
              setIsAuthenticating(false);
              resolve();
              if (options?.navigateOnSuccess === true) {
                navigate('/');
              } else if (typeof options?.navigateOnSuccess === 'string') {
                navigate(options.navigateOnSuccess);
              }
            })
            .catch(error => {
              setIsAuthenticating(false);
              console.error('Error fetching user after registration:', error);
              reject(error);
            });
        } else {
          setIsAuthenticating(false);
          reject(new Error('Registration failed'));
        }
      };

      xhr.onerror = function() {
        setIsAuthenticating(false);
        reject(new Error('Registration failed'));
      };

      try {
        xhr.open('POST', `${API_BASE}/auth/register`, true);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.withCredentials = true;
        xhr.send(JSON.stringify({ email, username, password }));
      } catch (error) {
        setIsAuthenticating(false);
        console.error('Registration error:', error);
        reject(error);
      }
    });
  }

  const logout = async () => {
    try {
      const response = await fetch(`${API_BASE}/auth/logout`, {
        method: 'POST',
        credentials: 'include'  // Important: sends cookies with request
      })

      if (!response.ok) {
        throw new Error('Logout failed')
      }
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      // Intentional sign-out — clear the "was logged in" flag so the post-
      // reload 401 path doesn't misread this as a stale-tab expiry.
      localStorage.removeItem('auth.wasLoggedIn')
      // Hard refresh — tears down all in-memory React Query / context state
      // so no authenticated data lingers after sign-out. This is DELIBERATE,
      // not a missed router navigation: see PageLayout's handleSignOut. Public
      // routes stay put as anon; the few ProtectedRoute pages bounce to /login
      // via their own guards. Do not "fix" this back to navigate('/').
      window.location.reload()
    }
  }

  return (
    <AuthContext.Provider value={{ user, login, register, logout, isLoading, isAuthenticating }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
